import { computed } from "vue";
import { defineStore, storeToRefs, acceptHMRUpdate } from "pinia";
import { useCurrentTranslationDiffSnapshotsQuery } from "@/features/experience-curation/queries/useTranslationDiffSnapshotsQuery";
import { useExperienceTranslationStore } from "@/features/experience-translation/stores/useExperienceTranslationStore";
import { useIsCurrentTranslationAutomatic } from "@/features/experience-translation/composables/useIsCurrentTranslationAutomatic";
import { mapSnapshotTranslations } from "@/features/experience-shared/utils/mapSnapshotTranslations";

/**
 * Store that exposes the snapshots used for the diff (latest published vs previous publication).
 * Uses a TanStack query for the latest snapshot and the translation store for the previous one.
 */
export const useCurrentTranslationDiffSnapshotsStore = defineStore("useCurrentTranslationDiffSnapshotsStore", () => {
  const translationDiffQuery = useCurrentTranslationDiffSnapshotsQuery();
  const translationStore = useExperienceTranslationStore();
  const { translation } = storeToRefs(translationStore);

  const latestEnglishSnapshot = computed(() => mapSnapshotTranslations(translationDiffQuery.data.value?.snapshot));
  const lastEnglishSnapshotBeforePublication = computed(() =>
    mapSnapshotTranslations(translation.value.lastEnglishSnapshotBeforePublication)
  );

  const hasDiff = computed(() => {
    if (useIsCurrentTranslationAutomatic().value) {
      return false;
    }

    return !!latestEnglishSnapshot.value && !!lastEnglishSnapshotBeforePublication.value;
  });

  return {
    latestEnglishSnapshot,
    lastEnglishSnapshotBeforePublication,
    hasDiff,
  };
});

// HMR
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCurrentTranslationDiffSnapshotsStore, import.meta.hot));
}
